/**
 * Filter URL utilities for building clean, SEO-friendly URLs and managing filter state
 */

import { SoftwareType, HolidayType } from '@/types/sequence';

export interface FilterParams {
  software?: string[];
  holiday?: string[];
  genres?: string[];
  genreSlugs?: string[];
  artists?: string[];
  fileFormats?: string[];
  albums?: string[];
  query?: string;
  minPrice?: number;
  maxPrice?: number;
  featured?: boolean;
  isFree?: boolean;
  sort?: string;
  page?: number;
}

export interface RouteParams {
  software?: string;
  holiday?: string;
  genre?: string;
  artist?: string;
}

/**
 * URL-safe slug mappings for filter values
 */
export const FILTER_SLUGS = {
  software: {
    'xLights': 'xlights',
    'LOR': 'lor'
  },
  holiday: {
    'Christmas': 'christmas',
    'Halloween': 'halloween', 
    'FourthOfJuly': 'fourth-of-july',
    'Easter': 'easter',
    'Thanksgiving': 'thanksgiving',
    'Hanukkah': 'hanukkah'
  }
} as const;

/**
 * Reverse mappings for converting slugs back to filter values
 */
export const SLUG_TO_FILTER = {
  software: Object.fromEntries(
    Object.entries(FILTER_SLUGS.software).map(([key, value]) => [value, key])
  ),
  holiday: Object.fromEntries(
    Object.entries(FILTER_SLUGS.holiday).map(([key, value]) => [value, key])
  )
} as const;

/**
 * Convert filter value to URL-safe slug
 */
export function toSlug(type: 'software' | 'holiday', value: string): string {
  const slugMap = FILTER_SLUGS[type] as Record<string, string>;
  return slugMap[value] || value.toLowerCase().replace(/\s+/g, '-');
}

/**
 * Convert URL slug back to filter value
 */
export function fromSlug(type: 'software' | 'holiday', slug: string): string {
  const filterMap = SLUG_TO_FILTER[type] as Record<string, string>;
  return filterMap[slug] || slug;
}

/**
 * Build path-based URL for filter combinations
 */
export function buildFilterPath(filters: {
  software?: string;
  holiday?: string;
  genre?: string;
  artist?: string;
}): string {
  const { software, holiday, genre, artist } = filters;
  
  // Base path
  let path = '/sequences';
  
  // Software-specific paths
  if (software && holiday) {
    path += `/${toSlug('software', software)}/${toSlug('holiday', holiday)}`;
  } else if (software) {
    path += `/${toSlug('software', software)}`;
  } else if (holiday) {
    path += `/holiday/${toSlug('holiday', holiday)}`;
  } else if (genre) {
    path += `/genre/${genre.toLowerCase().replace(/\s+/g, '-')}`;
  } else if (artist) {
    path += `/artist/${artist.toLowerCase().replace(/\s+/g, '-')}`;
  }
  
  return path;
}

/**
 * Build query string from filter parameters
 */
export function buildQueryString(filters: FilterParams): string {
  const params = new URLSearchParams();
  
  if (filters.query) params.set('q', filters.query);
  if (filters.software && filters.software.length > 0) {
    params.set('software', filters.software.join(','));
  }
  if (filters.holiday && filters.holiday.length > 0) {
    params.set('holiday', filters.holiday.join(','));
  }
  if (filters.genres && filters.genres.length > 0) {
    params.set('genres', filters.genres.join(','));
  }
  if (filters.artists && filters.artists.length > 0) {
    params.set('artists', filters.artists.join(','));
  }
  if (filters.fileFormats && filters.fileFormats.length > 0) {
    params.set('fileFormats', filters.fileFormats.join(','));
  }
  if (filters.albums && filters.albums.length > 0) {
    params.set('albums', filters.albums.join(','));
  }
  if (filters.minPrice !== undefined) {
    params.set('minPrice', filters.minPrice.toString());
  }
  if (filters.maxPrice !== undefined) {
    params.set('maxPrice', filters.maxPrice.toString());
  }
  if (filters.featured !== undefined) {
    params.set('featured', filters.featured.toString());
  }
  if (filters.isFree !== undefined) {
    params.set('free', filters.isFree.toString());
  }
  // Only add sort parameter if it's different from default
  // Note: Removed download_count reference as it doesn't exist in Typesense schema
  if (filters.sort && filters.sort !== '_text_match:desc,created_at:desc') {
    params.set('sort', filters.sort);
  }
  if (filters.page && filters.page > 1) {
    params.set('page', filters.page.toString());
  }
  
  return params.toString();
}

/**
 * Build complete URL with path and query parameters
 */
export function buildFilterUrl(
  pathFilters: { software?: string; holiday?: string; genre?: string; artist?: string },
  queryFilters: FilterParams = {}
): string {
  const path = buildFilterPath(pathFilters);
  const queryString = buildQueryString(queryFilters);
  
  return queryString ? `${path}?${queryString}` : path;
}

/**
 * Parse route parameters into filter state
 */
export function parseRouteFilters(params: RouteParams): FilterParams {
  const filters: FilterParams = {};
  
  if (params.software) {
    filters.software = [fromSlug('software', params.software)];
  }
  
  if (params.holiday) {
    filters.holiday = [fromSlug('holiday', params.holiday)];
  }
  
  if (params.genre) {
    // Prefer slug-based filtering via genre_slug; keep name as fallback for UI
    filters.genreSlugs = [params.genre.toLowerCase()];
    filters.genres = [params.genre.replace(/-/g, ' ')];
  }
  
  if (params.artist) {
    filters.artists = [params.artist.replace(/-/g, ' ')];
  }
  
  return filters;
}

/**
 * Merge route filters with query parameter filters
 */
export function mergeFilters(
  routeFilters: FilterParams,
  queryFilters: FilterParams
): FilterParams {
  return {
    ...queryFilters,
    // Route filters take precedence for their specific types
    software: routeFilters.software || queryFilters.software,
    holiday: routeFilters.holiday || queryFilters.holiday,
    genres: routeFilters.genres || queryFilters.genres,
    artists: routeFilters.artists || queryFilters.artists,
  };
}

/**
 * Generate breadcrumb navigation links
 */
export function generateBreadcrumbs(
  software?: string,
  holiday?: string,
  title?: string
): Array<{ label: string; href: string; isActive: boolean }> {
  const breadcrumbs = [
    { label: 'Home', href: '/', isActive: false },
    { label: 'Sequences', href: '/sequences', isActive: false }
  ];
  
  if (software) {
    breadcrumbs.push({
      label: software,
      href: buildFilterPath({ software }),
      isActive: !holiday && !title
    });
  }
  
  if (holiday) {
    // Independent holiday filtering (Option B)
    breadcrumbs.push({
      label: holiday,
      href: buildFilterPath({ holiday }),
      isActive: !title
    });
  }
  
  if (title) {
    breadcrumbs.push({
      label: title,
      href: '',
      isActive: true
    });
  }
  
  return breadcrumbs;
}

/**
 * Get category information for landing pages
 */
export function getCategoryInfo(type: 'software' | 'holiday', value: string): {
  title: string;
  description: string;
  keywords: string[];
} {
  const categoryInfo = {
    software: {
      xLights: {
        title: 'xLights Sequences',
        description: 'Professional lighting sequences designed for xLights software. Create stunning synchronized light displays for holidays and events.',
        keywords: ['xlights', 'lighting sequences', 'christmas lights', 'synchronized lights', 'light show']
      },
      LOR: {
        title: 'Light-O-Rama (LOR) Sequences', 
        description: 'High-quality lighting sequences compatible with Light-O-Rama controllers. Perfect for residential and commercial light displays.',
        keywords: ['light-o-rama', 'lor', 'lighting sequences', 'christmas lights', 'holiday lights']
      }
    },
    holiday: {
      Christmas: {
        title: 'Christmas Light Sequences',
        description: 'Magical Christmas lighting sequences to bring your holiday display to life. From classic carols to modern hits.',
        keywords: ['christmas lights', 'holiday sequences', 'christmas music', 'synchronized lights', 'holiday display']
      },
      Halloween: {
        title: 'Halloween Light Sequences',
        description: 'Spooky and fun Halloween lighting sequences perfect for trick-or-treaters and haunted displays.',
        keywords: ['halloween lights', 'spooky sequences', 'halloween music', 'haunted display', 'october lights']
      },
      'FourthOfJuly': {
        title: 'Fourth of July Light Sequences',
        description: 'Patriotic lighting sequences celebrating American independence with red, white, and blue displays.',
        keywords: ['fourth of july', 'patriotic lights', 'independence day', 'american sequences', 'july 4th']
      }
    }
  };
  
  const typeInfo = categoryInfo[type] as Record<string, any>;
  return typeInfo[value] || {
    title: `${value} Sequences`,
    description: `Browse our collection of ${value.toLowerCase()} lighting sequences.`,
    keywords: [value.toLowerCase(), 'lighting sequences', 'synchronized lights']
  };
}